import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { AuthService } from "@app/services/auth.service";
import { KbService } from "@app/services/kb.service";
import { QuoteService } from "@app/services/quote.service";
import { SearchService } from "@app/services/search.service";
import { eFilterControl, eFilterSource, eFilterType, eProductSortBy, ICategoryAttribute, ICategoryNode, IFilter, IProduct, IProductSearchArgs, IProductSuggestPayload, IRootScope, ISavedSearch, meta } from "@models";
import { KPromise } from "@rules";
import { icons } from "@tools";
import { IPromiseTracker } from "angular";
import { ISearchBinding, ISearchScope, SearchController } from "../search.view";

// tslint:disable:interface-name
interface $IProduct extends IProduct {
    $addQty?: number;
}
interface $ICategoryNode extends ICategoryNode {
    $parent?: ICategoryNode;
}

export interface IProductSavedSearch extends ISavedSearch{
    selectedCategoryId?: number;
}

export interface IProductsBinding extends ISearchBinding{
    selectedCategory: ICategoryNode;
    search?: IProductSavedSearch;
}

export interface IProductsScope extends ISearchScope<IProduct> {
    binding: IProductsBinding;
    categories: ICategoryNode[];
    imageMode: boolean;
    setToImageMode: () => void;
    setToListMode: () => void;
    addToQuote: (product: IProduct, qty: number) => void;
    translateSuggestion: (item: IProductSuggestPayload) => string;
    attributeDb: { [name: string]: ICategoryAttribute };
    getChildCategories: (cat: ICategoryNode) => number[];
}

@NgView({
    token: Token.ProductsView,
    dependencies: [
        Token.$Scope,
        Token.SearchService,
        Token.KbService,
        Token.QuoteService,
        Token.Categories
    ],
    resolves: [
        {
            name: "categories",
            dependencies: [
                Token.$RootScope,
                Token.AuthService,
                Token.ApiService
            ],
            fn: ($rootScope: IRootScope, authService: AuthService, api: ApiService) => {
                return authService.authPromise.then(() => {
                    return api.categories.tree({ onlyActiveCategories: true }, $rootScope.contentTracker).then(r => {
                        r.unshift({ name: loc.all, id: -1 } as ICategoryNode);
                        return r;
                    });
                });
            }
        }
    ]
})
export class ProductsController extends SearchController<IProduct> {

    constructor(
        protected $scope: IProductsScope,
        public agg: SearchService,
        private kbService: KbService,
        private quoteService: QuoteService,
        private categories: ICategoryNode[]
    ) {

        super($scope, agg, true);

        agg.$rootScope.setTitle(loc.products);

        agg.drawerService.setHelpUrl("Products");
        agg.drawerService.pageType = loc.products;
        agg.drawerService.pageIcon = icons.products;

        $scope.categories = categories;
        this.flatCategories = categories.flatten(c => c.items as ICategoryNode[]);
        $scope.attributeDb = {};
        for (let c of this.flatCategories) {
            if (c.attributes) {
                for (let a of c.attributes) {
                    $scope.attributeDb[a.id] = a;
                }
            }
        }
        $scope.savedSearchEnabled = false;
        $scope.customResults = true;
        
        if (agg.$state.params.imagemode) {
            $scope.imageMode = (agg.$state.params.imagemode == "true");
        } else {
            $scope.imageMode = agg.$rootScope.context.companySettings.displayProductsInImageModeByDefault;
        }

        $scope.setToImageMode = () => {
            $scope.imageMode = true;
            // pageSize = 35;
        };

        $scope.setToListMode = () => {
            $scope.imageMode = false;
            // pageSize = 11;
        };

        $scope.getChildCategories = (cat) => {
            return this.getChildCategories(cat);
        }
        
        $scope.translateSuggestion = item => {
            let name = item.name;
            if (agg.$rootScope.companySettings.defaultLanguage != agg.$rootScope.clientLanguage && item.translations) {
                let translation = item.translations.find(t => t.languageIso == agg.$rootScope.clientLanguage);
                if (translation) {
                    name = translation.name || item.name;
                }
            }
            return name;
        };


        // when the category selection is changed, fire a search
        $scope.$watch("binding.selectedCategory", (newVal: ICategoryNode, oldVal) => {
            if (newVal !== oldVal) {
                this.$scope.binding.search.selectedCategoryId = newVal.id;
                this.refreshAttributeFilters();
                $scope.reload();
            }
        });
      

        let addProductPromise: any = new KPromise(null);
        $scope.addToQuote = (product, qty) => {
            if (product.isConfigured) {
                agg.$state.go("kb.configurator", { id: product.id });
            } else {
                kbService.fly("#product_" + product.id.toString() + "_image", "#product-fly-target", () => {
                    addProductPromise = addProductPromise.then(() => {
                        return quoteService.addProduct({ idProduct: product.id, qty, addLocally: true });
                    });
                });
            }
        };

        this.init();
        this.loadSelectedCategory();
        this.refreshAttributeFilters();
    }

    protected flatCategories: ICategoryNode[] = [];

    public preInit() {
        
    }

    public client(api: ApiService) {
        return api.products;
    }
    
    public getQueryStringParams(): {} {
        return {imagemode: this.$scope.imageMode};
    }

    public searchCall(args: IProductSearchArgs, tracker: IPromiseTracker): ng.IPromise<IProduct[]> {
        args.categoryIds = [];
        if (this.$scope.binding.selectedCategory.id >= 0) {
            args.categoryIds = this.getChildCategories(this.$scope.binding.selectedCategory);
        }
        return this.client(this.agg.api).searchByCategory(args, tracker);
    }

    public loadedFromQueryString(search: IProductSavedSearch) {
        this.loadSelectedCategory();
    }

    public massageResults(items: $IProduct[]) {
        //add a property to the product for the UI to control the qty getting added to the quote
        items.forEach(p => p.$addQty = p.minQty);
    }

    public loadSelectedCategory() {
        if (this.$scope.binding.search.selectedCategoryId == null) this.$scope.binding.search.selectedCategoryId = -1;
        this.$scope.binding.selectedCategory = this.getCategory(this.$scope.categories, this.$scope.binding.search.selectedCategoryId);

        this.agg.$timeout(() => {
            this.expandToSelectedCategory();
        }, 0);
    }

    public newSearch(): IProductSavedSearch { 
        return {
            selectedCategoryId: -1,
            modelType: meta.Product.$name,
            sortField: "score",
            descending: true,
            filters: (() => {
                let filters: IFilter[] = [];
                filters.push(
                    {
                        type: eFilterType.sort,
                        label: loc.sortby,
                        control: eFilterControl.select,
                        sourceType: eFilterSource.productSorts,
                        valueField: "value",
                        labelField: "label",
                        values: [eProductSortBy.relevance],
                        sticky: true
                    }
                );

                filters.pushArray(this.getFiltersFromMeta(meta.Product.$name, true));
                return filters;
            })()
        };
    }

    protected getCategory(cats: ICategoryNode[], id: number): ICategoryNode {
        if (cats) {
            for (let cat of cats) {
                if (cat.id == id) {
                    return cat;
                }

                let match = this.getCategory(cat.items, id);
                if (match) {
                    return match;
                }
            }
        }

        return null;
    }

     /** gets an array of the selected category id and all of it's children id's used for filtering the search results*/
    protected getChildCategories(cat: ICategoryNode): number[] {
        let ids: number[] = [];
        ids.push(cat.id);

        cat.items.forEach(child => {
            ids = ids.concat(this.getChildCategories(child));
        });

        return ids;
    }

    protected expandToSelectedCategory() {
        let addParentRef = (cats: $ICategoryNode[], parent: $ICategoryNode) => {
            cats.forEach(cat => {
                cat.$parent = parent;
                if (cat.items) addParentRef(cat.items, cat);
            });
        };

        addParentRef(this.$scope.categories, null);

        let selectedAncestors: $ICategoryNode[] = [];
        let p = this.$scope.binding.selectedCategory as $ICategoryNode;
        while (p) {
            selectedAncestors.push(p);
            p = p.$parent;
        }

        selectedAncestors.forEach(a => a.expanded = true);
    }

    public refreshAttributeFilters() {
        let availableAtts = this.getSelectedCategoryAttributes();
        //remove available attribute filters
        this.$scope.availableFilters.removeWhere(f => f.type == eFilterType.attribute);
        //remove any existing filters that are referencing attributes no longer applicable
        this.$scope.binding.search.filters.removeWhere(f => f.type == eFilterType.attribute && !availableAtts.some(a => a.name == f.fieldName));

        let attFilters = this.$scope.binding.search.filters.filter(f => f.type == eFilterType.attribute);
        for (let a of availableAtts) {
            if (!attFilters.find(f => f.attributeId == a.id)) { //only add the attribute filter it if it's not already a filter
                let f: IFilter = {
                    type: eFilterType.attribute,
                    fieldName: a.name,
                    label: a.name,
                    fieldType: a.type,
                    sourceType: eFilterSource.attributes,
                    values: [null],
                    attributeId: a.id
                };
                if (a.default) {
                    this.$scope.binding.search.filters.push(f);
                } else {
                    this.$scope.availableFilters.push(f);
                }
            }
        }

        this.fillFilters(); //fill the filters again so it fills the attribute filters
    }

    protected getSelectedCategoryAttributes(): ICategoryAttribute[] {
        let parentCats: { [id: string]: ICategoryNode } = {};
        let selectedCat = this.$scope.binding.selectedCategory;
        this.flatCategories.filter(c => selectedCat.lft >= c.lft && selectedCat.rgt <= c.rgt).forEach(c => parentCats[c.id] = c);

        return Object.values(parentCats).selectMany<ICategoryAttribute>(c => c.attributes);
    }
}
